import { Alert, Box, Button, CircularProgress, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime, IANAZone } from "luxon";
import { useEffect, useState } from "react";
import { useModals } from "../../../context/ModalContext";
import { useGlobalSnackbar } from "../../../context/SnackbarContext";
import { gateway } from "../../../gateway";
import { selectCurrentCompany } from "../../../redux/features/user/selectors/userState.selectors";
import { useAppSelector } from "../../../redux/hooks/redux";
import { getJobsById } from "../../../utils";
import { getJobEvents } from "../../jobs/jobs-list/jobs-layout/actions/getJobEvents";
import { toCompanyScheduleInstant } from "./workforceScheduleTime";

type BlockKind = "SHIFT" | "ABSENCE" | "BREAK";
type ImpactedVisit = { uid: string; jobId: number; startDate: string; endDate: string };
type ScheduleBlock = { uid: string; kind: BlockKind; startAt: string; endAt: string; impactedVisits?: ImpactedVisit[] };

const labels: Record<BlockKind, string> = { SHIFT: "Shift", ABSENCE: "Absence", BREAK: "Break" };
const maxMinutes: Record<BlockKind, number> = { SHIFT: 24 * 60, ABSENCE: 31 * 24 * 60, BREAK: 8 * 60 };

const errorMessage = (error: any) => error?.response?.data?.message || error?.message || "Could not save the schedule block.";

const reassignmentReason = (kind: BlockKind) => {
  if (kind === "ABSENCE") return "This worker is recorded as absent during this visit, so someone else has to take it.";
  if (kind === "BREAK") return "This worker has a recorded break during this visit, so someone else has to take it.";
  return "This shift does not cover the visit, so the assignment has to be reviewed.";
};

export function WorkforceScheduleBlocks({ workforceUid, active }: { workforceUid: string; active: boolean }) {
  const company = useAppSelector(selectCurrentCompany);
  const zone = company?.timeZone || "";
  const validZone = IANAZone.isValidZone(zone);
  const today = validZone ? DateTime.now().setZone(zone).toISODate()! : "";
  const [selectedDay, setSelectedDay] = useState(today);
  const [kind, setKind] = useState<BlockKind>("SHIFT");
  const [startWall, setStartWall] = useState(today ? `${today}T09:00` : "");
  const [endWall, setEndWall] = useState(today ? `${today}T17:00` : "");
  const [formError, setFormError] = useState("");
  const queryClient = useQueryClient();
  const { showSnackbar } = useGlobalSnackbar();
  const { openModal, closeByKey } = useModals() || {};
  const [openingVisitUid, setOpeningVisitUid] = useState<string | null>(null);

  const openVisitAssignment = async (visit: ImpactedVisit) => {
    if (!openModal || !closeByKey) return;
    setOpeningVisitUid(visit.uid);
    try {
      const token = sessionStorage.getItem("token");
      const jobBody = await getJobsById({ token, id: visit.jobId });
      const job = jobBody?.data ?? jobBody;
      const events = await getJobEvents({ jobId: visit.jobId, page: 1, pageSize: 100, order: "ASC", orderBy: "startDate" });
      const event = (events?.items || []).find((item: { uid?: string; workforce?: unknown[] }) => item.uid === visit.uid);
      const modalKey = `Edit-Workforce-${visit.uid}`;
      const { JobApproved } = await import("../../jobs/jobs-list/jobs-layout/components/Approved/JobApproved");
      openModal(
        modalKey,
        <JobApproved
          data={job}
          mode="edit"
          currentWf={event?.workforce || []}
          jobEventUid={visit.uid}
          jobId={visit.jobId}
          availabilityWindow={{ startAt: visit.startDate, endAt: visit.endDate, excludeVisitUid: visit.uid }}
          onSaveWorkforce={async () => {
            await queryClient.invalidateQueries({ queryKey: ["workforce-schedule-blocks", company?.id, workforceUid] });
            closeByKey(modalKey);
          }}
        />,
      );
    } catch (error) {
      showSnackbar(errorMessage(error), "error");
    } finally {
      setOpeningVisitUid(null);
    }
  };

  useEffect(() => {
    if (today && !selectedDay) setSelectedDay(today);
    if (today && !startWall) setStartWall(`${today}T09:00`);
    if (today && !endWall) setEndWall(`${today}T17:00`);
  }, [today, selectedDay, startWall, endWall]);

  const rangeStart = selectedDay && validZone ? DateTime.fromISO(selectedDay, { zone }).startOf("day") : null;
  const queryKey = ["workforce-schedule-blocks", company?.id, workforceUid, selectedDay];
  const blocksQuery = useQuery<ScheduleBlock[]>({
    queryKey,
    enabled: active && !!workforceUid && !!rangeStart?.isValid,
    queryFn: async () => {
      const response = await gateway.get(`/workforce/${workforceUid}/schedule-blocks`, {
        params: { startAt: rangeStart!.toUTC().toISO(), endAt: rangeStart!.plus({ days: 7 }).toUTC().toISO() },
      });
      return response.data?.data || [];
    },
  });

  const create = useMutation({
    mutationFn: async (payload: { kind: BlockKind; startAt: string; endAt: string }) =>
      gateway.post(`/workforce/${workforceUid}/schedule-blocks`, payload),
    onSuccess: async (_response, payload) => {
      setFormError("");
      setSelectedDay(DateTime.fromISO(payload.startAt).setZone(zone).toISODate()!);
      await queryClient.invalidateQueries({ queryKey: ["workforce-schedule-blocks", company?.id, workforceUid] });
      showSnackbar("Schedule block saved", "success");
    },
    onError: (error) => setFormError(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: async (blockUid: string) => gateway.delete(`/workforce/${workforceUid}/schedule-blocks/${blockUid}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["workforce-schedule-blocks", company?.id, workforceUid] });
      showSnackbar("Schedule block removed", "success");
    },
    onError: (error) => setFormError(errorMessage(error)),
  });

  const handleSave = () => {
    setFormError("");
    if (!validZone) return setFormError("Set the company time zone before adding schedule blocks.");
    const startAt = toCompanyScheduleInstant(startWall, zone);
    const endAt = toCompanyScheduleInstant(endWall, zone);
    if (!startAt || !endAt) return setFormError("Enter valid, unambiguous dates and times in the company time zone.");
    const duration = DateTime.fromISO(endAt).diff(DateTime.fromISO(startAt), "minutes").minutes;
    if (duration <= 0 || duration > maxMinutes[kind]) return setFormError(`The ${labels[kind].toLowerCase()} must last more than zero and at most ${maxMinutes[kind] / 60} hours.`);
    create.mutate({ kind, startAt, endAt });
  };

  const formatBlock = (value: string) => DateTime.fromISO(value, { setZone: true }).setZone(zone).toFormat("ccc, LLL d · h:mm a");

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>Worker schedule</Typography>
        <Typography variant="body2" color="text.secondary">Record shifts, absences and breaks. Times use {validZone ? zone : "the company time zone"}. A recorded shift alone does not confirm attendance.</Typography>
      </Box>
      {!validZone && <Alert severity="warning">Set a valid company time zone before using this schedule.</Alert>}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <TextField select size="small" label="Type" value={kind} onChange={(event) => setKind(event.target.value as BlockKind)} sx={{ minWidth: 145 }}>
          {(Object.keys(labels) as BlockKind[]).map((value) => <MenuItem key={value} value={value}>{labels[value]}</MenuItem>)}
        </TextField>
        <TextField size="small" label="From" type="datetime-local" value={startWall} onChange={(event) => setStartWall(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
        <TextField size="small" label="To" type="datetime-local" value={endWall} onChange={(event) => setEndWall(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
        <Button variant="contained" onClick={handleSave} disabled={!validZone || create.isPending} sx={{ whiteSpace: "nowrap", flexShrink: 0, minWidth: 104 }}>Add block</Button>
      </Stack>
      {formError && <Alert severity="error" onClose={() => setFormError("")}>{formError}</Alert>}
      <TextField size="small" label="Week starting" type="date" value={selectedDay} onChange={(event) => setSelectedDay(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ maxWidth: 220 }} disabled={!validZone} />
      {blocksQuery.isLoading && <CircularProgress size={22} />}
      {blocksQuery.isError && <Alert severity="error">{errorMessage(blocksQuery.error)}</Alert>}
      {!blocksQuery.isLoading && !blocksQuery.isError && blocksQuery.data?.length === 0 && <Typography variant="body2" color="text.secondary">No blocks recorded for these seven days.</Typography>}
      {blocksQuery.data?.map((block) => (
        <Box key={block.uid} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, borderBottom: "1px solid #eee", pb: 1 }}>
          <Box>
            <Typography variant="body2"><strong>{labels[block.kind]}</strong> · {formatBlock(block.startAt)} – {formatBlock(block.endAt)}</Typography>
            {!!block.impactedVisits?.length && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {block.impactedVisits.length} {block.impactedVisits.length === 1 ? "visit needs" : "visits need"} reassignment:
                <Typography variant="body2" sx={{ mt: 0.5 }}>{reassignmentReason(block.kind)}</Typography>
                {block.impactedVisits.slice(0, 5).map(visit => (
                  <Box key={visit.uid} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 0.75 }}>
                    <Typography variant="body2">Job #{visit.jobId} · {formatBlock(visit.startDate)}</Typography>
                    <Button size="small" disabled={openingVisitUid === visit.uid} onClick={() => openVisitAssignment(visit)} sx={{ whiteSpace: "nowrap" }}>
                      Edit assignment
                    </Button>
                  </Box>
                ))}
                {block.impactedVisits.length > 5 && <Typography variant="body2">And {block.impactedVisits.length - 5} more.</Typography>}
              </Alert>
            )}
          </Box>
          <Button size="small" color="error" disabled={remove.isPending} onClick={() => remove.mutate(block.uid)}>Remove</Button>
        </Box>
      ))}
    </Stack>
  );
}
